// Tiller Sync > Upload page. Chunked upload flow for unbounded file size on
// Workers Free:
//
//   1. POST /uploads/raw   — multipart xlsx → R2 archive + dedup, returns
//                            { upload_id, duplicate_of_prior? }
//   2. GET  /uploads/sync-state — per-account latest dates
//   3. Browser parses xlsx with SheetJS (no CPU limit)
//   4. Filter parsed rows down to the delta vs sync-state
//   5. Send rows in CHUNK_SIZE batches to /uploads/:id/chunk
//   6. POST /uploads/:id/finalize → returns parse_summary + opens ResolutionsModal

import { useCallback, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, accounts, uploads } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { ResolutionsModal } from '@/components/ResolutionsModal';
import { useMe } from '@/hooks/useMe';
import { cn } from '@/lib/cn';
import {
  buildSyncStateMap,
  buildUnmatchedHints,
  collapseLatestBalancePerAccount,
  filterBalancesBySyncState,
  filterTransactionsBySyncState,
  parseTillerWorkbookClient,
} from '@/lib/tiller-parser';
import type {
  UploadChunkBalance,
  UploadChunkTransaction,
  UploadFinalizeResponse,
  UploadParseError,
  UploadResolutionsRequest,
} from '@frt/shared-types';

const CHUNK_SIZE = 100;

interface ProgressState {
  phase:
    | 'idle'
    | 'archiving'
    | 'syncing'
    | 'parsing'
    | 'filtering'
    | 'uploading'
    | 'finalizing'
    | 'done';
  uploadId?: number;
  totalChunks?: number;
  chunkIndex?: number;
  message?: string;
}

export function TillerUploadPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  const [progress, setProgress] = useState<ProgressState>({ phase: 'idle' });
  const [finalized, setFinalized] = useState<UploadFinalizeResponse | null>(null);
  const [uploadError, setUploadError] = useState<{ code: string; message: string; priorId?: number } | null>(
    null,
  );
  const [resolveError, setResolveError] = useState<string | null>(null);

  const canUpload = !!me && (me.role === 'admin' || me.role === 'senior');

  const accountsQuery = useQuery({
    queryKey: ['accounts', 'all-for-mapping'],
    queryFn: () => accounts.list({ per_page: 500 }),
    staleTime: 60_000,
    enabled: !!finalized,
  });

  const resolutionsMutation = useMutation({
    mutationFn: (vars: { uploadId: number; body: UploadResolutionsRequest }) =>
      uploads.resolutions(vars.uploadId, vars.body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
      queryClient.invalidateQueries({ queryKey: ['mapping'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setFinalized(null);
      setProgress({ phase: 'idle' });
    },
    onError: (err: unknown) => {
      setResolveError(err instanceof ApiError ? err.message : String(err));
    },
  });

  const reset = useCallback(() => {
    setPickedFile(null);
    setFinalized(null);
    setUploadError(null);
    setResolveError(null);
    setProgress({ phase: 'idle' });
  }, []);

  const runUpload = useCallback(async (file: File) => {
    setUploadError(null);
    setFinalized(null);
    try {
      // Step 1 — archive xlsx
      setProgress({ phase: 'archiving', message: 'Archiving file…' });
      const buf = await file.arrayBuffer();
      const raw = await uploads.raw(file, file.name);
      if (raw.duplicate_of_prior) {
        setUploadError({
          code: 'DUPLICATE_UPLOAD',
          message: 'This file has already been uploaded.',
          priorId: raw.duplicate_of_prior,
        });
        setProgress({ phase: 'idle' });
        return;
      }
      const uploadId = raw.upload_id;

      // Step 2 — sync state
      setProgress({ phase: 'syncing', uploadId, message: 'Querying server sync state…' });
      const syncState = await uploads.syncState();
      const stateMap = buildSyncStateMap(syncState.accounts);

      // Step 3 — parse client-side
      setProgress({ phase: 'parsing', uploadId, message: 'Parsing workbook…' });
      const parsed = await parseTillerWorkbookClient(buf);
      const rowsParsed = {
        balances: parsed.balances.length,
        transactions: parsed.transactions.length,
      };

      // Step 4 — filter to delta + collapse balances to latest-per-account
      setProgress({ phase: 'filtering', uploadId, message: 'Filtering to delta…' });
      const collapsedBalances = collapseLatestBalancePerAccount(parsed.balances);
      const balancesToSend = filterBalancesBySyncState(collapsedBalances, stateMap);
      const transactionsToSend = filterTransactionsBySyncState(parsed.transactions, stateMap);

      // Collect the union of seen Tiller IDs (from BOTH balances and transactions
      // before sync-state filtering — server uses this to compute missing/unmatched
      // diff against ALL Tiller accounts the file referenced).
      const seenTillerIds = new Set<string>();
      for (const b of parsed.balances) seenTillerIds.add(b.tiller_account_id);
      for (const t of parsed.transactions) seenTillerIds.add(t.tiller_account_id);
      const unmatchedHints = buildUnmatchedHints(collapsedBalances);

      // Step 5 — chunked POSTs
      const chunks = buildChunks(balancesToSend, transactionsToSend, CHUNK_SIZE);
      setProgress({
        phase: 'uploading',
        uploadId,
        totalChunks: chunks.length,
        chunkIndex: 0,
        message: chunks.length === 0 ? 'Nothing new to upload' : `Uploading chunk 1 / ${chunks.length}…`,
      });
      for (let i = 0; i < chunks.length; i++) {
        await uploads.chunk(uploadId, chunks[i]!);
        setProgress({
          phase: 'uploading',
          uploadId,
          totalChunks: chunks.length,
          chunkIndex: i + 1,
          message: `Uploaded chunk ${i + 1} / ${chunks.length}`,
        });
      }

      // Step 6 — finalize
      setProgress({
        phase: 'finalizing',
        uploadId,
        totalChunks: chunks.length,
        chunkIndex: chunks.length,
        message: 'Finalizing…',
      });
      const final = await uploads.finalize(uploadId, {
        rows_parsed: rowsParsed,
        errors: parsed.errors as UploadParseError[],
        seen_tiller_ids: [...seenTillerIds],
        unmatched_hints: unmatchedHints,
      } as Parameters<typeof uploads.finalize>[1]);
      setFinalized(final);
      setProgress({ phase: 'done', uploadId, message: 'Done' });
      setPickedFile(null);
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
    } catch (err) {
      const code =
        err instanceof ApiError ? err.code : (err as { code?: string })?.code ?? 'UPLOAD_FAILED';
      const message = err instanceof Error ? err.message : String(err);
      const priorId =
        err instanceof ApiError && err.code === 'DUPLICATE_UPLOAD'
          ? (err.details as { prior_upload_id?: number } | undefined)?.prior_upload_id
          : undefined;
      setUploadError({ code, message, priorId });
      setProgress({ phase: 'idle' });
    }
  }, [queryClient]);

  const handlePick = useCallback((file: File | null) => {
    setPickedFile(file);
    setFinalized(null);
    setUploadError(null);
  }, []);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handlePick(file);
  };

  if (!canUpload) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upload Tiller file</CardTitle>
        </CardHeader>
        <CardBody className="text-sm text-slate-600">
          Uploading a Tiller feed requires admin or senior role.
        </CardBody>
      </Card>
    );
  }

  const isWorking = progress.phase !== 'idle' && progress.phase !== 'done';
  const progressPct =
    progress.phase === 'uploading' && progress.totalChunks
      ? Math.round(((progress.chunkIndex ?? 0) / Math.max(1, progress.totalChunks)) * 100)
      : progress.phase === 'finalizing'
      ? 100
      : 0;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Upload Tiller file</h1>

      <Card>
        <CardBody>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-12 cursor-pointer',
              dragActive
                ? 'border-brand-500 bg-brand-50'
                : 'border-slate-300 bg-slate-50 hover:bg-slate-100',
            )}
          >
            <p className="text-base font-medium text-slate-700">
              Drag and drop your Tiller_SN_Basic_Feed.xlsx here
            </p>
            <p className="text-sm text-slate-500">or click to choose a file</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
            />
            {pickedFile && (
              <p className="mt-3 text-sm text-slate-700">
                Selected: <span className="font-mono">{pickedFile.name}</span> (
                {(pickedFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="ghost" onClick={reset} disabled={isWorking}>
              Reset
            </Button>
            <Button
              onClick={() => pickedFile && runUpload(pickedFile)}
              disabled={!pickedFile || isWorking}
            >
              {isWorking ? 'Working…' : 'Upload and parse'}
            </Button>
          </div>

          {isWorking && (
            <div className="mt-4">
              <div className="mb-1 text-xs text-slate-600">{progress.message}</div>
              <div className="h-2 w-full rounded bg-slate-200 overflow-hidden">
                <div
                  className="h-full bg-brand-500 transition-all"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              {progress.totalChunks ? (
                <div className="mt-1 text-xs text-slate-500 tabular-nums">
                  {progress.chunkIndex ?? 0} / {progress.totalChunks} chunks
                </div>
              ) : null}
            </div>
          )}

          {uploadError && (
            <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <div className="font-medium">{uploadError.code}</div>
              <div>{uploadError.message}</div>
              {uploadError.priorId && (
                <div className="mt-1 text-xs">
                  This file was previously uploaded as #{uploadError.priorId}.
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {finalized && (
        <Card>
          <CardHeader>
            <CardTitle>Parse summary — upload #{finalized.upload.id}</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Balances added" value={finalized.parse_summary.balances_added} />
              <Stat label="Transactions added" value={finalized.parse_summary.transactions_added} />
              <Stat
                label="Missing app accounts"
                value={finalized.parse_summary.missing_app_accounts.length}
              />
              <Stat
                label="Unmatched Tiller accounts"
                value={finalized.parse_summary.unmatched_tiller_accounts.length}
              />
            </div>
            {finalized.parse_summary.errors.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-amber-700">
                  {finalized.parse_summary.errors.length} per-row error(s)
                </summary>
                <ul className="mt-1 list-disc pl-6">
                  {finalized.parse_summary.errors.slice(0, 25).map((e, i) => (
                    <li key={i}>
                      [{e.code}] {e.sheet ? `${e.sheet}:` : ''}
                      {e.row ? `row ${e.row}` : ''} {e.message}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </CardBody>
        </Card>
      )}

      <ResolutionsModal
        open={!!finalized}
        uploadId={finalized?.upload.id ?? 0}
        filename={finalized?.upload.filename ?? ''}
        missing={finalized?.parse_summary.missing_app_accounts ?? []}
        unmatched={finalized?.parse_summary.unmatched_tiller_accounts ?? []}
        appAccounts={accountsQuery.data?.data ?? []}
        submitting={resolutionsMutation.isPending}
        errorMessage={resolveError}
        onCancel={() => setFinalized(null)}
        onSubmit={(body) =>
          finalized && resolutionsMutation.mutate({ uploadId: finalized.upload.id, body })
        }
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function buildChunks(
  balances: UploadChunkBalance[],
  transactions: UploadChunkTransaction[],
  chunkSize: number,
): { balances: UploadChunkBalance[]; transactions: UploadChunkTransaction[] }[] {
  const out: { balances: UploadChunkBalance[]; transactions: UploadChunkTransaction[] }[] = [];
  // Stream balances first, then transactions. Each chunk holds up to chunkSize
  // ROWS TOTAL across both lists so a single Worker request stays bounded.
  let bIdx = 0;
  let tIdx = 0;
  while (bIdx < balances.length || tIdx < transactions.length) {
    const chunk: { balances: UploadChunkBalance[]; transactions: UploadChunkTransaction[] } = {
      balances: [],
      transactions: [],
    };
    while (chunk.balances.length + chunk.transactions.length < chunkSize) {
      if (bIdx < balances.length) {
        chunk.balances.push(balances[bIdx]!);
        bIdx++;
      } else if (tIdx < transactions.length) {
        chunk.transactions.push(transactions[tIdx]!);
        tIdx++;
      } else {
        break;
      }
    }
    out.push(chunk);
  }
  return out;
}
